import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { BarChart3, Activity, Users, Sparkles } from 'lucide-react';
import { useAdminAnalytics } from '../../hooks/useAdminAnalytics';

function formatDelta(delta: number) {
  const sign = delta > 0 ? '+' : '';
  return `${sign}${Math.round(delta * 100)}%`;
}

export function AdminAnalytics() {
  const { data, isLoading, isError } = useAdminAnalytics();

  if (isLoading || !data) {
    return (
      <main className="min-h-screen bg-[#070916] pb-24 text-white" aria-label="Loading admin analytics">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <p className="text-sm text-gray-400">Loading analytics…</p>
        </div>
      </main>
    );
  }

  if (isError) {
    return (
      <main className="min-h-screen bg-[#070916] pb-24 text-white" aria-label="Admin analytics error">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <p className="text-sm text-red-300">Unable to load analytics.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#070916] pb-24" aria-labelledby="admin-analytics-title">
      <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 id="admin-analytics-title" className="text-3xl font-semibold text-white sm:text-4xl">Platform analytics</h1>
            <p className="text-sm text-gray-400">Monitor growth, engagement, and operational health across Lighthouse.</p>
          </div>
          <Button className="bg-gold-gradient text-black hover:brightness-110">
            <Sparkles className="mr-2 h-4 w-4" aria-hidden />
            Generate weekly digest
          </Button>
        </div>

        <section className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {data.stats.map((stat) => (
            <Card key={stat.label} className="border-white/10 bg-white/[0.03]">
              <CardContent className="space-y-3 p-6">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{stat.label}</p>
                <p className="text-3xl font-semibold text-white">
                  {stat.label.includes('hrs') ? `${stat.value}` : stat.value.toLocaleString()}
                </p>
                <p className="text-xs text-[#f7ef8a]">{formatDelta(stat.delta)}</p>
              </CardContent>
            </Card>
          ))}
        </section>

        <section className="mt-12 grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-white">
                <BarChart3 className="h-5 w-5 text-[#ba9036]" aria-hidden />
                Weekly activity
              </CardTitle>
              <Badge className="border-white/10 bg-white/[0.05] text-gray-200">Last 7 days</Badge>
            </CardHeader>
            <CardContent className="space-y-4 p-6 pt-0 text-sm text-gray-300">
              <div className="grid gap-3 text-white sm:grid-cols-3">
                {data.charts.weeklyActivity.map((entry) => (
                  <div key={entry.label} className="rounded-md border border-white/10 bg-white/[0.02] p-3">
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{entry.label}</p>
                    <p className="text-2xl font-semibold">{entry.value.toLocaleString()}</p>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Insights</p>
                <ul className="mt-3 space-y-2 text-gray-200">
                  {data.insights.map((insight) => (
                    <li key={insight}>• {insight}</li>
                  ))}
                </ul>
              </div>
            </CardContent>
          </Card>

          <Card className="border-white/10 bg-white/[0.03]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-white">
                <Activity className="h-5 w-5 text-[#ba9036]" aria-hidden />
                Operational health
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-6 pt-0">
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Moderation backlog</p>
                <p className="text-lg font-semibold text-white">{data.backlog.moderation} listings</p>
                <p className="text-xs text-gray-400">SLA: {data.backlog.slaHours} hrs · P95 currently {data.backlog.p95Hours} hrs</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Support volume</p>
                <p className="text-lg font-semibold text-white">{data.backlog.supportTickets} tickets</p>
                <p className="text-xs text-gray-400">Returner feedback: ⚑ pending integration</p>
              </div>
              <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 space-y-2">
                <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Incidents</p>
                {data.charts.incidentHistory.map((entry) => (
                  <p key={entry.label} className="text-sm text-gray-300">
                    {entry.label}: {entry.value}
                  </p>
                ))}
              </div>
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                View detailed dashboards
              </Button>
              <Button variant="ghost" className="w-full text-[#f7ef8a] hover:text-[#f7ef8a]">
                Share weekly snapshot
              </Button>
            </CardContent>
          </Card>
        </section>
      </div>
    </main>
  );
}
